import classNames from "classnames";
import styles from "./styles.module.scss";
import InputFile from "../InputFile";
import UserGuidePopover from "../UserGuidePopover";
import { ImageData } from "../../mock";
import AccentLight from "../AccentLight";
import { Spin } from "antd";
import { toDataURLByFile } from "src/utils/toDataURL";
import { getImageProductDescription } from "../../api";
import { GenerateService } from "@services/generate/generate.service";

interface AsinTabProps {
  items: string[];
  onChange: (index: number) => void;
  onUploadImage: (file: File | null) => void;
  isLoading?: boolean;
  index: number;
  setIndex: (index: number) => void;
  UserGuide: boolean;
  staticData?: ImageData[];
  currStep: number;
  setCurrUserStepGuide: (value: number) => void;
  setShowModal: (value: boolean) => void;
  setUserGuide: (value: boolean) => void;
  setTemplates: (value: any) => void;
  setFileId: (value: string) => void;
}

const AsinTab: React.FC<AsinTabProps> = ({
  items,
  onChange,
  onUploadImage,
  isLoading = false,
  index,
  setIndex,
  UserGuide,
  staticData,
  currStep,
  setCurrUserStepGuide,
  setShowModal,
  setUserGuide,
  setTemplates,
  setFileId,
}) => {
  const generateService = new GenerateService();
  const allowInteraction = !isLoading && !UserGuide;

  const handleChange = (i: number) => {
    if (!allowInteraction) return;
    onChange(i);
    setIndex(i);
  };

  const handleUploadImage = async (file: File | null) => {
    if (!allowInteraction) return;
    onUploadImage(file);
    if (!file) return;

    const imageDataUrl = await toDataURLByFile(file);
    const description = await getImageProductDescription(imageDataUrl);

    setTemplates((prev) => {
      const updatedTransformed: any[] = [];
      for (const template of prev.original) {
        const updatedList: string[] = [];
        for (const el of template.list) {
          updatedList.push(el.replace("[product]", description));
        }
        updatedTransformed.push({ ...template, list: updatedList });
      }
      return {
        ...prev,
        transformed: updatedTransformed,
      };
    });

    const res = await generateService.fileUpload(file);
    setFileId(res.file_id);
  };

  const renderAssetItem = (imageUrl: string | ImageData, i: number) => {
    const isActive = i === index;
    const disabled = UserGuide && i !== 0;
    const thumb =
      UserGuide && typeof imageUrl !== "string"
        ? imageUrl.thumb
        : (imageUrl as string);

    const button = (
      <button
        type="button"
        disabled={disabled}
        className={classNames(
          styles.image,
          isActive && styles["image--active"],
          disabled && styles["image--disabled"],
        )}
        style={{ backgroundImage: `url(${thumb})` }}
        onClick={() => handleChange(i)}
      />
    );

    if (UserGuide && i === 0) {
      return (
        <li key={i}>
          <AccentLight
            customCSS={{ borderRadius: "8px", border: "1px solid #407ab1" }}
          >
            {button}
          </AccentLight>
        </li>
      );
    }

    return <li key={i}>{button}</li>;
  };

  const renderAssets = () => {
    const data = UserGuide && staticData ? staticData : items;
    return <ul className={styles.assets}>{data.map(renderAssetItem)}</ul>;
  };

  const content = renderAssets();

  return (
    <>
      {UserGuide ? (
        <AccentLight customCSS={{ borderRadius: "50px" }}>
          <InputFile onChange={handleUploadImage} />
        </AccentLight>
      ) : (
        <InputFile onChange={handleUploadImage} />
      )}

      <div
        className={classNames(
          styles.assetsWrapper,
          isLoading && styles.loading,
        )}
      >
        {isLoading && <Spin size="large" className={styles.spin} />}
        {UserGuide && currStep === 0 ? (
          <UserGuidePopover
            placement="rightTop"
            step={currStep}
            text={0}
            nextStep={1}
            setCurrUserStepGuide={setCurrUserStepGuide}
            additionalHandlers={{ setShowModal }}
            setUserGuide={setUserGuide}
          >
            {content}
          </UserGuidePopover>
        ) : (
          content
        )}
      </div>

      <button hidden className="button button--sm" onClick={() => setIndex(-1)}>
        reset
      </button>
    </>
  );
};

export default AsinTab;
